import React from "react"
import { motion } from 'framer-motion'
import loginSide from '../../Assets/Images/loginSide.png'
import logo from '../../Assets/Images/rklogo.png'
import FieldInput from '../../Components/FieldInput'
import * as Yup from 'yup'
import { useFormik } from "formik"
import Button from "../../Components/Button"
import { FiLogIn } from "react-icons/fi";
import { useHistory } from "react-router-dom"
import API from "../../Common/API"
import { useDispatch } from "react-redux"
import { setAlert } from "../../Store/Theme/actions"
import { setAccountType, setLoggedIn, setToken, setUserDetails } from "../../Store/Auth/actions"
import ForgotPasswordModal from "../../Components/ForgotPasswordModal"

const initialLoginForm = {
    userName: '',
    password: ''
}

const Login = () => {
    const history = useHistory()
    const dispatch = useDispatch()
    const [forgotOpen, setForgotOpen] = React.useState(false)

    const loginFormSchema = Yup.object().shape({
        userName: Yup.string().required("User name is required"),
        password: Yup.string().required("Password is required")
    })

    const loginForm = useFormik({
        initialValues: initialLoginForm,
        validateOnMount: true,
        validationSchema: loginFormSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            API.post('/auth/login', values).then(async (apiRes) => {
                if (apiRes.status !== 200 || !apiRes.data?.data) {
                    dispatch(setAlert({ title: 'Login failed', subtitle: apiRes.data?.error || 'Unexpected server response', type: 'error', active: true }))
                    return
                }
                const token = apiRes.data.data
                const userDetails = JSON.parse(window.atob(token.split(".")[1]))
                if (userDetails.role === 'Super_Admin' || userDetails.role === 'Admin') {
                    localStorage.setItem('token', token)
                    localStorage.setItem('accountType', 'admin')
                    localStorage.setItem('userDetails', JSON.stringify(userDetails))
                    dispatch(setUserDetails(userDetails))
                    dispatch(setAccountType('admin'))
                    dispatch(setToken(token))
                    dispatch(setLoggedIn(true))
                    history.push('/')
                } else {
                    // Member credentials — redirect them to the member login page
                    history.push('/member/login')
                }
            }).catch((err) => {
                const backendMessage =
                    err?.response?.data?.error ||
                    err?.response?.data?.message ||
                    err?.message ||
                    'Unable to login. Please try again.'
                console.error('Admin login failed', err?.response || err)
                dispatch(setAlert({ title: 'Login failed', subtitle: backendMessage, type: 'error', active: true }))
            })
        }
    })

    return (
        <>
        <motion.div className="min-h-screen w-full px-4 py-5 md:px-8">
            <div className="mx-auto grid min-h-[calc(100vh-2.5rem)] max-w-[1320px] grid-cols-1 overflow-hidden rounded-[36px] border border-white/70 bg-white/55 shadow-soft backdrop-blur-xl lg:grid-cols-[1.05fr_0.95fr]">
                <div className="relative hidden min-h-full overflow-hidden bg-tColor lg:block">
                    <img
                        src={loginSide}
                        alt="Matrimony welcome"
                        className="absolute inset-0 h-full w-full object-cover opacity-70"
                    />
                    <div className="absolute inset-0 bg-gradient-to-br from-tColor/85 via-tColor/35 to-primeDark/75" />
                    <div className="relative z-10 flex h-full flex-col justify-between p-10 text-white">
                        <div className="flex items-center gap-4">
                            <div className="flex h-16 w-16 items-center justify-center rounded-3xl bg-white/90">
                                <img src={logo} alt="Royal Kavery" className="h-12 w-12 object-contain" />
                            </div>
                            <div>
                                <p className="text-xs font-bold uppercase tracking-[0.28em] text-white/70">Royal Kavery</p>
                                <h1 className="mt-1 text-3xl font-semibold">Matrimony Admin</h1>
                            </div>
                        </div>
                        <div className="max-w-xl">
                            <p className="text-sm font-bold uppercase tracking-[0.28em] text-primeSoft">Trusted Profile Operations</p>
                            <h2 className="mt-4 text-5xl font-semibold leading-tight">A calmer way to manage profiles, plans, and approvals.</h2>
                            <p className="mt-5 text-lg leading-8 text-white/82">Review members, keep subscriptions healthy, and support families with a cleaner admin workflow.</p>
                        </div>
                    </div>
                </div>

                <div className="flex items-center justify-center px-5 py-10 md:px-12">
                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="w-full max-w-[520px]"
                    >
                        <div className="mb-9 lg:hidden">
                            <img src={logo} alt="Royal Kavery" className="h-16 w-16 object-contain" />
                        </div>
                        <p className="text-xs font-bold uppercase tracking-[0.28em] text-primeColor">Secure Admin Login</p>
                        <h1 className="mt-3 text-4xl font-semibold text-tColor md:text-5xl">Welcome back</h1>
                        <p className="mt-4 max-w-md text-base leading-7 text-tpColor">Sign in with your Super Admin account to manage the matrimony platform.</p>
                        <div className="mt-4 flex items-center gap-2 rounded-2xl border border-borderColor bg-primeSoft/60 px-4 py-3">
                            <span className="text-sm text-tpColor">Are you a member?</span>
                            <button
                                type="button"
                                className="text-sm font-bold text-primeColor underline underline-offset-2 hover:text-primeDark"
                                onClick={() => history.push('/member/login')}
                            >
                                Go to Member Login →
                            </button>
                        </div>

                        <div className="mt-9 rounded-[30px] border border-borderColor bg-white/90 p-5 shadow-soft md:p-7">
                            <div className="grid grid-cols-1 gap-4">
                                <FieldInput outerInjectClass="w-full" label="User Name" fieldProps={loginForm.getFieldProps('userName')} fieldHelper={loginForm.getFieldHelpers('userName')} fieldMeta={loginForm.getFieldMeta('userName')} edit={true} dataLoaded={true} />
                                <FieldInput type="password" outerInjectClass="w-full" label="Password" fieldProps={loginForm.getFieldProps('password')} fieldHelper={loginForm.getFieldHelpers('password')} fieldMeta={loginForm.getFieldMeta('password')} edit={true} dataLoaded={true} />
                                <div className="flex flex-col gap-3 text-sm font-semibold text-tpColor sm:flex-row sm:items-center sm:justify-between">
                                    <label className="flex items-center gap-2">
                                        <input type="checkbox" className="h-4 w-4 rounded border-borderColor accent-primeColor" />
                                        Remember me
                                    </label>
                                    <button type="button" onClick={() => setForgotOpen(true)} className="font-semibold text-primeColor hover:underline">Forgot password?</button>
                                </div>
                                <Button type="primary" injectClass="mt-2 w-full" onClick={() => loginForm.submitForm()}>
                                    <FiLogIn /> Login
                                </Button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </motion.div>
        <ForgotPasswordModal open={forgotOpen} onClose={() => setForgotOpen(false)} />
        </>
    )
}

export default Login
